import { useMemo, useState, useEffect, useCallback } from 'react';
import { toLocalDateStr } from '../lib/utils';
import { getStoredGitAliases, resolveGitAuthor, type GitAlias } from './SettingsView';
import * as api from '@/services/api';
import { isDoneStatus, isForwardStatus, isReworkStatus } from '@/services/jira-status-config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faArrowTrendDown,
  faArrowTrendUp,
  faBolt,
  faBullseye,
  faChartBar,
  faChartLine,
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faClock,
  faCodeBranch,
  faCodeCommit,
  faFileCode,
  faFire,
  faRotate,
  faShieldHalved,
  faSpinner,
  faTriangleExclamation,
  faUserCheck,
  faUsers
} from '@fortawesome/free-solid-svg-icons';

const mkIcon = (def: import('@fortawesome/fontawesome-svg-core').IconDefinition) =>
  ({ className = '' }: { className?: string }) =>
    <FontAwesomeIcon icon={def} className={className} />;

// ==================== Types ====================

interface GitCommitItem {
  hash: string;
  author: string;
  email: string;
  date: string;
  message: string;
  branch: string;
  additions: number;
  deletions: number;
  isMerge?: boolean;
  repoName: string;
  repoId: string;
}

interface JiraEvent {
  id: string;
  timestamp: string;
  type: string;
  issueKey: string;
  issueSummary: string;
  issueType: string;
  author: { displayName: string; accountId: string; avatarUrl?: string };
  fromStatus?: string;
  toStatus?: string;
  fromAssignee?: string;
  toAssignee?: string;
  fromSprint?: string;
  toSprint?: string;
  fromValue?: string;
  toValue?: string;
}

interface SprintSummary {
  name: string;
  state: string;
  completedIssues: number;
  totalIssues: number;
}

interface LinkedGroup {
  issueKey: string;
  issueSummary?: string;
  issueType?: string;
  latestStatus?: string;
  commits: GitCommitItem[];
  jiraEvents: JiraEvent[];
}

interface Indicator {
  id: string;
  name: string;
  description: string;
  value: number;
  label: string;
  severity: 'good' | 'warning' | 'critical';
  icon: any;
  details?: string[];
  baseline: string;
}

interface LagIndicatorDevResult {
  developerName: string;
  value: number;
  label: string;
  severity: 'good' | 'warning' | 'critical';
}

interface LagIndicatorResult {
  id: number;
  name: string;
  description: string;
  scope?: 'per-user' | 'overall';
  results: LagIndicatorDevResult[];
  overallValue: number;
  overallLabel: string;
  severity: 'good' | 'warning' | 'critical';
}

interface CombinedIndicatorsProps {
  gitCommits: GitCommitItem[];
  jiraEvents: JiraEvent[];
  sprintSummary: SprintSummary[];
  linkedItems: LinkedGroup[];
  lookbackDays: number;
  startDate?: string;
  endDate?: string;
  isDark: boolean;
  jiraDomain?: string;
  jiraEmail?: string;
  jiraApiToken?: string;
  selectedJiraProject?: string;
}

// ==================== Helpers ====================

const SEVERITY_COLORS = {
  good: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', dot: 'bg-emerald-400', ring: 'ring-emerald-500/30' },
  warning: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', dot: 'bg-amber-400', ring: 'ring-amber-500/30' },
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', dot: 'bg-red-400', ring: 'ring-red-500/30' },
};

function extractJiraKeys(text: string): string[] {
  const matches = text.match(/[A-Z][A-Z0-9]+-\d+/g);
  return matches ? [...new Set(matches.map(k => k.toUpperCase()))] : [];
}

// ==================== Indicator Computations ====================

function computeGitLagIndicators(commits: GitCommitItem[], _lookbackDays: number, aliases: GitAlias[] = []): Indicator[] {
  if (commits.length === 0) return [];
  const indicators: Indicator[] = [];

  // 1. Large Commits — commits with >300 total lines changed
  const largeThreshold = 300;
  const largeCommits = commits.filter(c => (c.additions + c.deletions) > largeThreshold);
  const largePct = commits.length > 0 ? (largeCommits.length / commits.length) * 100 : 0;
  indicators.push({
    id: 'git-lag-large-commits',
    name: 'Large Commits',
    description: `Commits with >${largeThreshold} lines changed are harder to review and more likely to introduce bugs`,
    value: largeCommits.length,
    label: `${largeCommits.length} of ${commits.length} (${largePct.toFixed(0)}%)`,
    severity: largePct > 30 ? 'critical' : largePct > 15 ? 'warning' : 'good',
    icon: mkIcon(faFileCode),
    baseline: '✓ <15% | ⚠ 15–30% | ✗ >30%',
    details: largeCommits.slice(0, 5).map(c => `${c.hash.slice(0, 7)} — ${c.additions + c.deletions} lines — ${c.message.slice(0, 60)}`),
  });

  // 2. Unlinked Commits — commits without Jira ticket references
  const unlinked = commits.filter(c => {
    const keys = [...extractJiraKeys(c.message), ...extractJiraKeys(c.branch)];
    return keys.length === 0;
  });
  const unlinkedPct = commits.length > 0 ? (unlinked.length / commits.length) * 100 : 0;
  indicators.push({
    id: 'git-lag-unlinked',
    name: 'Unlinked Commits',
    description: 'Commits without Jira ticket references lack traceability',
    value: unlinked.length,
    label: `${unlinked.length} of ${commits.length} (${unlinkedPct.toFixed(0)}%)`,
    severity: unlinkedPct > 50 ? 'critical' : unlinkedPct > 25 ? 'warning' : 'good',
    icon: mkIcon(faArrowRightArrowLeft),
    baseline: '✓ <25% | ⚠ 25–50% | ✗ >50%',
    details: unlinked.slice(0, 5).map(c => `${c.hash.slice(0, 7)} — ${c.message.slice(0, 70)}`),
  });

  // 3. Commit Concentration (Bus Factor) — if one author has >60% of commits
  const authorCounts = new Map<string, number>();
  for (const c of commits) { const resolved = resolveGitAuthor(c.author, aliases); authorCounts.set(resolved, (authorCounts.get(resolved) || 0) + 1); }
  const sorted = [...authorCounts.entries()].sort((a, b) => b[1] - a[1]);
  const topAuthorPct = sorted.length > 0 ? (sorted[0][1] / commits.length) * 100 : 0;
  const uniqueAuthors = sorted.length;
  indicators.push({
    id: 'git-lag-concentration',
    name: 'Commit Concentration',
    description: 'High concentration in one author increases bus factor risk',
    value: topAuthorPct,
    label: uniqueAuthors === 1 ? `Single author (${sorted[0]?.[0]})` : `Top author: ${topAuthorPct.toFixed(0)}% (${sorted[0]?.[0]})`,
    severity: topAuthorPct > 80 ? 'critical' : topAuthorPct > 60 ? 'warning' : 'good',
    icon: mkIcon(faUsers),
    baseline: '✓ <60% | ⚠ 60–80% | ✗ >80%',
    details: sorted.slice(0, 5).map(([name, count]) => `${name}: ${count} commits (${((count / commits.length) * 100).toFixed(0)}%)`),
  });

  // 4. Weekend/Off-hours Work — commits outside 8am-7pm weekdays (burnout risk)
  const offHours = commits.filter(c => {
    const d = new Date(c.date);
    const day = d.getDay();
    const hour = d.getHours();
    return day === 0 || day === 6 || hour < 8 || hour >= 19;
  });
  const offHoursPct = commits.length > 0 ? (offHours.length / commits.length) * 100 : 0;
  indicators.push({
    id: 'git-lag-offhours',
    name: 'Off-Hours Work',
    description: 'Commits outside business hours (8am-7pm weekdays) may indicate burnout risk',
    value: offHours.length,
    label: `${offHours.length} of ${commits.length} (${offHoursPct.toFixed(0)}%)`,
    severity: offHoursPct > 40 ? 'critical' : offHoursPct > 20 ? 'warning' : 'good',
    icon: mkIcon(faClock),
    baseline: '✓ <20% | ⚠ 20–40% | ✗ >40%',
    details: offHours.slice(0, 5).map(c => {
      const d = new Date(c.date);
      return `${c.hash.slice(0, 7)} — ${d.toLocaleDateString('en-AU', { weekday: 'short' })} ${d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} — ${resolveGitAuthor(c.author, aliases)}`;
    }),
  });

  return indicators;
}

function computeGitLeadIndicators(commits: GitCommitItem[], _lookbackDays: number, aliases: GitAlias[] = []): Indicator[] {
  if (commits.length === 0) return [];
  const indicators: Indicator[] = [];

  // 1. Commit Frequency — average commits per active day
  const daySet = new Set(commits.map(c => toLocalDateStr(new Date(c.date))));
  const activeDays = daySet.size;
  const commitsPerDay = activeDays > 0 ? commits.length / activeDays : 0;
  indicators.push({
    id: 'git-lead-frequency',
    name: 'Commit Frequency',
    description: 'Consistent, frequent commits indicate healthy development flow',
    value: commitsPerDay,
    label: `${commitsPerDay.toFixed(1)} commits/active day (${activeDays} active days)`,
    severity: commitsPerDay >= 3 ? 'good' : commitsPerDay >= 1.5 ? 'warning' : 'critical',
    icon: mkIcon(faChartLine),
    baseline: '✓ ≥3/day | ⚠ 1.5–3/day | ✗ <1.5/day',
  });

  // 2. Small Focused Commits — average lines per commit
  const totalLines = commits.reduce((sum, c) => sum + c.additions + c.deletions, 0);
  const avgLines = commits.length > 0 ? totalLines / commits.length : 0;
  indicators.push({
    id: 'git-lead-small-commits',
    name: 'Small Focused Commits',
    description: 'Smaller commits are easier to review, test, and revert',
    value: avgLines,
    label: `${avgLines.toFixed(0)} avg lines/commit`,
    severity: avgLines <= 100 ? 'good' : avgLines <= 250 ? 'warning' : 'critical',
    icon: mkIcon(faBullseye),
    baseline: '✓ ≤100 | ⚠ 100–250 | ✗ >250 lines',
  });

  // 3. Active Contributors — unique authors (resolved via aliases)
  const authors = new Set(commits.map(c => resolveGitAuthor(c.author, aliases)));
  indicators.push({
    id: 'git-lead-contributors',
    name: 'Active Contributors',
    description: 'Multiple active contributors reduce knowledge silos',
    value: authors.size,
    label: `${authors.size} unique contributor${authors.size !== 1 ? 's' : ''}`,
    severity: authors.size >= 4 ? 'good' : authors.size >= 2 ? 'warning' : 'critical',
    icon: mkIcon(faUserCheck),
    baseline: '✓ ≥4 | ⚠ 2–3 | ✗ 1',
    details: [...authors].slice(0, 10).map(a => a),
  });

  // 4. Linked Commit Rate — % of commits with Jira references
  const linked = commits.filter(c => {
    const keys = [...extractJiraKeys(c.message), ...extractJiraKeys(c.branch)];
    return keys.length > 0;
  });
  const linkedPct = commits.length > 0 ? (linked.length / commits.length) * 100 : 0;
  indicators.push({
    id: 'git-lead-linked-rate',
    name: 'Traceability Rate',
    description: 'Commits referencing Jira tickets provide traceability and context',
    value: linkedPct,
    label: `${linkedPct.toFixed(0)}% of commits linked to Jira`,
    severity: linkedPct >= 70 ? 'good' : linkedPct >= 40 ? 'warning' : 'critical',
    icon: mkIcon(faArrowRightArrowLeft),
    baseline: '✓ ≥70% | ⚠ 40–70% | ✗ <40%',
  });

  // 5. Branch Diversity — work across multiple branches
  const branches = new Set(commits.map(c => c.branch));
  indicators.push({
    id: 'git-lead-branches',
    name: 'Branch Diversity',
    description: 'Work across feature branches indicates organized development',
    value: branches.size,
    label: `${branches.size} active branch${branches.size !== 1 ? 'es' : ''}`,
    severity: branches.size >= 5 ? 'good' : branches.size >= 2 ? 'warning' : 'critical',
    icon: mkIcon(faCodeBranch),
    baseline: '✓ ≥5 | ⚠ 2–4 | ✗ 1',
  });

  return indicators;
}

function computeJiraLagIndicators(events: JiraEvent[], sprintSummary: SprintSummary[]): Indicator[] {
  if (events.length === 0) return [];
  const indicators: Indicator[] = [];

  // 1. Status Thrashing — issues that bounce between statuses (>2 status changes)
  const issueStatusChanges = new Map<string, number>();
  for (const e of events) {
    if (e.type === 'status_change') {
      issueStatusChanges.set(e.issueKey, (issueStatusChanges.get(e.issueKey) || 0) + 1);
    }
  }
  const thrashingIssues = [...issueStatusChanges.entries()].filter(([, count]) => count > 3);
  const thrashPct = issueStatusChanges.size > 0 ? (thrashingIssues.length / issueStatusChanges.size) * 100 : 0;
  indicators.push({
    id: 'jira-lag-thrashing',
    name: 'Status Thrashing',
    description: 'Issues with >3 status changes may indicate unclear requirements or rework',
    value: thrashingIssues.length,
    label: `${thrashingIssues.length} issue${thrashingIssues.length !== 1 ? 's' : ''} (${thrashPct.toFixed(0)}%)`,
    severity: thrashPct > 30 ? 'critical' : thrashPct > 15 ? 'warning' : 'good',
    icon: mkIcon(faRotate),
    baseline: '✓ <15% | ⚠ 15–30% | ✗ >30%',
    details: thrashingIssues.slice(0, 5).map(([key, count]) => `${key}: ${count} status changes`),
  });

  // 2. Dev Rework — issues moved back to development-like statuses
  let reworkCount = 0;
  const issueLastForward = new Map<string, boolean>();
  const sortedEvents = [...events].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  for (const e of sortedEvents) {
    if (e.type === 'status_change' && e.toStatus) {
      if (isForwardStatus(e.toStatus)) {
        issueLastForward.set(e.issueKey, true);
      } else if (isReworkStatus(e.toStatus) && issueLastForward.get(e.issueKey)) {
        reworkCount++;
        issueLastForward.set(e.issueKey, false);
      }
    }
  }
  const uniqueIssues = new Set(events.map(e => e.issueKey)).size;
  const reworkPct = uniqueIssues > 0 ? (reworkCount / uniqueIssues) * 100 : 0;
  indicators.push({
    id: 'jira-lag-rework',
    name: 'Dev Rework Rate',
    description: 'Issues sent back to development after reaching testing/review',
    value: reworkCount,
    label: `${reworkCount} rework event${reworkCount !== 1 ? 's' : ''} (${reworkPct.toFixed(0)}% of issues)`,
    severity: reworkPct > 25 ? 'critical' : reworkPct > 10 ? 'warning' : 'good',
    icon: mkIcon(faFire),
    baseline: '✓ <10% | ⚠ 10–25% | ✗ >25%',
  });

  // 3. Unassigned Activity — events on issues that had assignment changes to "unassigned"
  const unassignedEvents = events.filter(e => e.type === 'assignment' && !e.toAssignee);
  indicators.push({
    id: 'jira-lag-unassigned',
    name: 'Unassignment Events',
    description: 'Issues being unassigned may indicate ownership confusion',
    value: unassignedEvents.length,
    label: `${unassignedEvents.length} unassignment${unassignedEvents.length !== 1 ? 's' : ''}`,
    severity: unassignedEvents.length > 10 ? 'critical' : unassignedEvents.length > 3 ? 'warning' : 'good',
    icon: mkIcon(faTriangleExclamation),
    baseline: '✓ ≤3 | ⚠ 4–10 | ✗ >10',
  });

  // 4. Sprint Completion Rate (from sprint summary)
  const activeSprints = sprintSummary.filter(s => s.state === 'active' || s.state === 'closed');
  if (activeSprints.length > 0) {
    const totalCompleted = activeSprints.reduce((sum, s) => sum + s.completedIssues, 0);
    const totalIssues = activeSprints.reduce((sum, s) => sum + s.totalIssues, 0);
    const completionRate = totalIssues > 0 ? (totalCompleted / totalIssues) * 100 : 0;
    const incompletePct = 100 - completionRate;
    indicators.push({
      id: 'jira-lag-sprint-incomplete',
      name: 'Sprint Incompletion',
      description: 'Percentage of sprint items not completed — indicates overcommitment',
      value: incompletePct,
      label: `${incompletePct.toFixed(0)}% incomplete (${totalIssues - totalCompleted}/${totalIssues})`,
      severity: incompletePct > 40 ? 'critical' : incompletePct > 20 ? 'warning' : 'good',
      icon: mkIcon(faBullseye),
      baseline: '✓ <20% | ⚠ 20–40% | ✗ >40%',
      details: activeSprints.map(s => `${s.name}: ${s.completedIssues}/${s.totalIssues} completed (${s.state})`),
    });
  }

  // 5. Stale Issues — issues with last activity >7 days ago (excluding done issues)
  const now = Date.now();
  const issueLastActivity = new Map<string, number>();
  const issueLastStatus = new Map<string, string>();
  for (const e of events) {
    const ts = new Date(e.timestamp).getTime();
    if (!issueLastActivity.has(e.issueKey) || ts > issueLastActivity.get(e.issueKey)!) {
      issueLastActivity.set(e.issueKey, ts);
    }
    if (e.type === 'status_change' && e.toStatus) {
      const prevTs = issueLastActivity.get(e.issueKey) || 0;
      if (ts >= prevTs) issueLastStatus.set(e.issueKey, e.toStatus.toLowerCase());
    }
  }
  const openIssues = [...issueLastActivity.entries()].filter(([key]) => {
    const lastStatus = issueLastStatus.get(key) || '';
    return !isDoneStatus(lastStatus);
  });
  const staleIssues = openIssues.filter(([, ts]) => (now - ts) > 7 * 86400000);
  const stalePct = openIssues.length > 0 ? (staleIssues.length / openIssues.length) * 100 : 0;
  indicators.push({
    id: 'jira-lag-stale',
    name: 'Stale Issues',
    description: 'Issues with no activity in the last 7 days — may indicate blockers or neglect',
    value: staleIssues.length,
    label: `${staleIssues.length} issue${staleIssues.length !== 1 ? 's' : ''} (${stalePct.toFixed(0)}%)`,
    severity: stalePct > 40 ? 'critical' : stalePct > 20 ? 'warning' : 'good',
    icon: mkIcon(faClock),
    baseline: '✓ <20% | ⚠ 20–40% | ✗ >40%',
    details: staleIssues.slice(0, 5).map(([key, ts]) => `${key}: last activity ${Math.round((now - ts) / 86400000)}d ago`),
  });

  return indicators;
}

function computeJiraLeadIndicators(events: JiraEvent[], sprintSummary: SprintSummary[], _linkedItems: LinkedGroup[]): Indicator[] {
  if (events.length === 0) return [];
  const indicators: Indicator[] = [];

  // 1. Throughput — unique issues with activity per week
  const uniqueIssuesByWeek = new Map<string, Set<string>>();
  for (const e of events) {
    const d = new Date(e.timestamp);
    const weekKey = `${d.getFullYear()}-W${Math.ceil((d.getDate() + new Date(d.getFullYear(), d.getMonth(), 1).getDay()) / 7)}`;
    if (!uniqueIssuesByWeek.has(weekKey)) uniqueIssuesByWeek.set(weekKey, new Set());
    uniqueIssuesByWeek.get(weekKey)!.add(e.issueKey);
  }
  const weeklyAvg = uniqueIssuesByWeek.size > 0
    ? [...uniqueIssuesByWeek.values()].reduce((sum, s) => sum + s.size, 0) / uniqueIssuesByWeek.size
    : 0;
  indicators.push({
    id: 'jira-lead-throughput',
    name: 'Weekly Throughput',
    description: 'Average unique issues touched per week — indicates team velocity',
    value: weeklyAvg,
    label: `${weeklyAvg.toFixed(1)} issues/week avg`,
    severity: weeklyAvg >= 15 ? 'good' : weeklyAvg >= 8 ? 'warning' : 'critical',
    icon: mkIcon(faChartBar),
    baseline: '✓ ≥15/wk | ⚠ 8–15/wk | ✗ <8/wk',
  });

  // 2. Sprint Completion Rate (positive framing)
  const activeSprints = sprintSummary.filter(s => s.state === 'active' || s.state === 'closed');
  if (activeSprints.length > 0) {
    const totalCompleted = activeSprints.reduce((sum, s) => sum + s.completedIssues, 0);
    const totalIssues = activeSprints.reduce((sum, s) => sum + s.totalIssues, 0);
    const completionRate = totalIssues > 0 ? (totalCompleted / totalIssues) * 100 : 0;
    indicators.push({
      id: 'jira-lead-sprint-completion',
      name: 'Sprint Completion Rate',
      description: 'Percentage of sprint items completed — indicates predictability',
      value: completionRate,
      label: `${completionRate.toFixed(0)}% (${totalCompleted}/${totalIssues})`,
      severity: completionRate >= 80 ? 'good' : completionRate >= 60 ? 'warning' : 'critical',
      icon: mkIcon(faCircleCheck),
      baseline: '✓ ≥80% | ⚠ 60–80% | ✗ <60%',
      details: activeSprints.map(s => `${s.name}: ${s.completedIssues}/${s.totalIssues} (${s.state})`),
    });
  }

  // 3. Team Collaboration — unique people with activity
  const uniqueAuthors = new Set(events.map(e => e.author.displayName));
  indicators.push({
    id: 'jira-lead-team-size',
    name: 'Active Team Members',
    description: 'Number of people with Jira activity — indicates team engagement',
    value: uniqueAuthors.size,
    label: `${uniqueAuthors.size} active member${uniqueAuthors.size !== 1 ? 's' : ''}`,
    severity: uniqueAuthors.size >= 5 ? 'good' : uniqueAuthors.size >= 3 ? 'warning' : 'critical',
    icon: mkIcon(faUsers),
    baseline: '✓ ≥5 | ⚠ 3–4 | ✗ <3',
    details: [...uniqueAuthors].slice(0, 10).map(a => a),
  });

  // 4. Resolution Activity — issues with completed/done status changes
  const resolved = events.filter(e => {
    if (e.type !== 'status_change' || !e.toStatus) return false;
    return isDoneStatus(e.toStatus);
  });
  const resolvedIssues = new Set(resolved.map(e => e.issueKey));
  indicators.push({
    id: 'jira-lead-resolved',
    name: 'Issues Resolved',
    description: 'Issues moved to completed/done status in the period',
    value: resolvedIssues.size,
    label: `${resolvedIssues.size} issue${resolvedIssues.size !== 1 ? 's' : ''} resolved`,
    severity: resolvedIssues.size >= 20 ? 'good' : resolvedIssues.size >= 8 ? 'warning' : 'critical',
    icon: mkIcon(faBolt),
    baseline: '✓ ≥20 | ⚠ 8–20 | ✗ <8',
  });

  // 5. High Priority Resolution — % of bugs/hotfixes that got resolved
  const highPriorityTypes = events.filter(e => e.issueType.toLowerCase() === 'bug' || e.issueType.toLowerCase() === 'hotfix');
  const highPriorityKeys = new Set(highPriorityTypes.map(e => e.issueKey));
  const highResolved = new Set([...resolvedIssues].filter(k => highPriorityKeys.has(k)));
  const highResPct = highPriorityKeys.size > 0 ? (highResolved.size / highPriorityKeys.size) * 100 : 0;
  indicators.push({
    id: 'jira-lead-high-priority',
    name: 'High Priority Resolution',
    description: 'Percentage of bug/hotfix issues that were resolved in the period',
    value: highResPct,
    label: `${highResPct.toFixed(0)}% (${highResolved.size}/${highPriorityKeys.size})`,
    severity: highPriorityKeys.size === 0 ? 'good' : highResPct >= 80 ? 'good' : highResPct >= 50 ? 'warning' : 'critical',
    icon: mkIcon(faShieldHalved),
    baseline: '✓ ≥80% | ⚠ 50–80% | ✗ <50%',
  });

  return indicators;
}

// ==================== Component ====================

const INDICATOR_ICONS: Record<number, any> = {
  1: mkIcon(faRotate), 2: mkIcon(faCircleCheck), 3: mkIcon(faTriangleExclamation), 4: mkIcon(faClock),
  5: mkIcon(faCodeCommit), 6: mkIcon(faFire), 7: mkIcon(faChartLine), 8: mkIcon(faBullseye), 9: mkIcon(faTriangleExclamation),
  10: mkIcon(faCircleCheck), 11: mkIcon(faShieldHalved), 12: mkIcon(faUsers), 13: mkIcon(faBolt), 14: mkIcon(faArrowTrendUp),
  15: mkIcon(faBullseye), 16: mkIcon(faTriangleExclamation), 17: mkIcon(faChartLine), 18: mkIcon(faClock),
};

export function CombinedIndicators({ gitCommits, jiraEvents, sprintSummary, linkedItems, lookbackDays, startDate, endDate, isDark, jiraDomain, jiraEmail, jiraApiToken, selectedJiraProject }: CombinedIndicatorsProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [serverIndicators, setServerIndicators] = useState<LagIndicatorResult[]>([]);
  const [serverLeadIndicators, setServerLeadIndicators] = useState<LagIndicatorResult[]>([]);
  const [serverLoading, setServerLoading] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Fetch server-calculated Jira lag indicators
  const fetchServerIndicators = useCallback(async () => {
    if (!jiraDomain || !jiraEmail || !jiraApiToken || !selectedJiraProject) return;
    setServerLoading(true);
    try {
      const data = await api.getJiraLagIndicators(
        jiraDomain!, jiraEmail!, jiraApiToken!, selectedJiraProject!, [], lookbackDays, startDate, endDate
      );
      if (data.indicators) setServerIndicators(data.indicators);
      // Also fetch lead indicators
      try {
        const leadData = await api.getJiraLeadIndicators(
          jiraDomain!, jiraEmail!, jiraApiToken!, selectedJiraProject!, [], lookbackDays, startDate, endDate
        );
        if (leadData.indicators) setServerLeadIndicators(leadData.indicators);
      } catch { /* lead indicators non-critical */ }
    } catch { /* ignore */ }
    finally { setServerLoading(false); }
  }, [jiraDomain, jiraEmail, jiraApiToken, selectedJiraProject, lookbackDays, startDate, endDate]);

  useEffect(() => { fetchServerIndicators(); }, [fetchServerIndicators]);

  const gitAliases = useMemo(() => getStoredGitAliases(), []);
  const gitLag = useMemo(() => computeGitLagIndicators(gitCommits, lookbackDays, gitAliases), [gitCommits, lookbackDays, gitAliases]);
  const gitLead = useMemo(() => computeGitLeadIndicators(gitCommits, lookbackDays, gitAliases), [gitCommits, lookbackDays, gitAliases]);
  const jiraLagComputed = useMemo(() => computeJiraLagIndicators(jiraEvents, sprintSummary), [jiraEvents, sprintSummary]);
  const jiraLeadComputed = useMemo(() => computeJiraLeadIndicators(jiraEvents, sprintSummary, linkedItems), [jiraEvents, sprintSummary, linkedItems]);

  // Merge overall-scoped server indicators into the overall Jira tables
  const overallServerLag = useMemo(() => serverIndicators.filter(i => (i.scope || 'per-user') === 'overall').map(ind => ({
    id: `server-lag-${ind.id}`,
    name: ind.name,
    description: ind.description,
    value: ind.overallValue,
    label: ind.overallLabel || String(ind.overallValue),
    severity: ind.severity,
    icon: INDICATOR_ICONS[ind.id] || mkIcon(faChartBar),
    baseline: ind.description,
  } as Indicator)), [serverIndicators]);

  const overallServerLead = useMemo(() => serverLeadIndicators.filter(i => (i.scope || 'per-user') === 'overall').map(ind => ({
    id: `server-lead-${ind.id}`,
    name: ind.name,
    description: ind.description,
    value: ind.overallValue,
    label: ind.overallLabel || String(ind.overallValue),
    severity: ind.severity,
    icon: INDICATOR_ICONS[ind.id] || mkIcon(faArrowTrendUp),
    baseline: ind.description,
  } as Indicator)), [serverLeadIndicators]);

  const jiraLag = useMemo(() => [...jiraLagComputed, ...overallServerLag], [jiraLagComputed, overallServerLag]);
  const jiraLead = useMemo(() => [...jiraLeadComputed, ...overallServerLead], [jiraLeadComputed, overallServerLead]);

  const allIndicators = [...gitLead, ...jiraLead, ...gitLag, ...jiraLag];
  const goodCount = allIndicators.filter(i => i.severity === 'good').length;
  const warningCount = allIndicators.filter(i => i.severity === 'warning').length;
  const criticalCount = allIndicators.filter(i => i.severity === 'critical').length;

  if (gitCommits.length === 0 && jiraEvents.length === 0) return null;

  // Compact table for our computed indicators (Indicator | Status | Value)
  const renderTable = (title: string, icon: any, indicators: Indicator[], theme: 'lead' | 'lag') => {
    if (indicators.length === 0) return null;
    const Icon = icon;
    const sGood = indicators.filter(i => i.severity === 'good').length;
    const sWarn = indicators.filter(i => i.severity === 'warning').length;
    const sCrit = indicators.filter(i => i.severity === 'critical').length;

    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 px-1">
          <Icon className={`h-3.5 w-3.5 ${theme === 'lead' ? 'text-emerald-400' : 'text-red-400'}`} />
          <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{title}</span>
          <div className="flex items-center gap-1 ml-auto">
            {sGood > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">{sGood} ✓</span>}
            {sWarn > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/20">{sWarn} ⚠</span>}
            {sCrit > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/20">{sCrit} ✗</span>}
          </div>
        </div>
        <div className={`overflow-x-auto rounded-lg border ${isDark ? 'border-slate-700/30' : 'border-slate-200'}`}>
          <table className="w-full text-[11px] table-fixed">
            <colgroup>
              <col style={{ width: '40%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '52%' }} />
            </colgroup>
            <thead>
              <tr className={isDark ? 'bg-slate-900/50' : 'bg-slate-50'}>
                <th className={`text-left px-2 py-1.5 text-[10px] font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Indicator</th>
                <th className={`text-center px-1 py-1.5 text-[10px] font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}></th>
                <th className={`text-right px-2 py-1.5 text-[10px] font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Value</th>
              </tr>
            </thead>
            <tbody>
              {indicators.map(ind => {
                const colors = SEVERITY_COLORS[ind.severity];
                const IndIcon = ind.icon;
                const isExpanded = expandedRows.has(ind.id);
                const hasDetails = ind.details && ind.details.length > 0;
                return (
                  <tr key={ind.id}
                    onClick={() => hasDetails && toggleRow(ind.id)}
                    className={`border-t ${isDark ? 'border-slate-700/15 hover:bg-slate-700/10' : 'border-slate-100 hover:bg-slate-50'} ${hasDetails ? 'cursor-pointer' : ''}`}>
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1.5">
                        <IndIcon className={`h-3 w-3 flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                        <span className={`text-[11px] ${isDark ? 'text-slate-300' : 'text-slate-700'} truncate`}>{ind.name}</span>
                        {hasDetails && (
                          isExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-2.5 w-2.5 text-slate-600 flex-shrink-0 ml-auto" /> : <FontAwesomeIcon icon={faChevronRight} className="h-2.5 w-2.5 text-slate-600 flex-shrink-0 ml-auto" />
                        )}
                      </div>
                      {isExpanded && (
                        <div className="mt-1 space-y-0.5 pl-4">
                          <div className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{ind.description}</div>
                          {ind.details?.map((d, i) => (
                            <div key={i} className={`text-[10px] font-mono ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{d}</div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-1 py-1 text-center">
                      <span className={`inline-block w-5 h-5 leading-5 rounded text-[10px] font-bold text-center ${colors.bg} ${colors.text} border ${colors.border}`}>
                        {ind.severity === 'good' ? '✓' : ind.severity === 'warning' ? '⚠' : '✗'}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right">
                      <div className={`text-[10px] font-mono ${colors.text} truncate`}>{ind.label}</div>
                      <div className={`text-[8px] font-mono ${isDark ? 'text-slate-600' : 'text-slate-400'} truncate`}>{ind.baseline}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Per-developer table for server-calculated Jira lag indicators (matching JiraLagIndicators embedded style)
  const renderServerLagTable = () => {
    const perUserIndicators = serverIndicators.filter(i => (i.scope || 'per-user') === 'per-user');
    if (perUserIndicators.length === 0 && !serverLoading) return null;

    // Build dev list sorted by activity
    const devActivity = new Map<string, number>();
    for (const ind of perUserIndicators) {
      for (const r of ind.results) {
        devActivity.set(r.developerName, (devActivity.get(r.developerName) || 0) + Math.abs(r.value));
      }
    }
    const allDevNames = Array.from(devActivity.entries()).sort((a, b) => b[1] - a[1]).map(([name]) => name);
    const visibleDevs = showAllUsers ? allDevNames : allDevNames.slice(0, 6);
    const hiddenCount = allDevNames.length - 6;
    const getDevResult = (indicator: LagIndicatorResult, devName: string) => indicator.results.find(r => r.developerName === devName);

    const sGood = perUserIndicators.filter(i => i.severity === 'good').length;
    const sWarn = perUserIndicators.filter(i => i.severity === 'warning').length;
    const sCrit = perUserIndicators.filter(i => i.severity === 'critical').length;

    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 px-1">
          <FontAwesomeIcon icon={faChartBar} className="h-3.5 w-3.5 text-indigo-400" />
          <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Per User Lag Indicators</span>
          <div className="flex items-center gap-1 ml-auto">
            {serverLoading && <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin text-blue-400" />}
            {sGood > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">{sGood} ✓</span>}
            {sWarn > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/20">{sWarn} ⚠</span>}
            {sCrit > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/20">{sCrit} ✗</span>}
          </div>
        </div>
        {perUserIndicators.length > 0 && (
          <div className={`overflow-x-auto rounded-lg border ${isDark ? 'border-slate-700/30' : 'border-slate-200'}`}>
            <table className="w-full text-[11px]">
              <thead>
                <tr className={isDark ? 'bg-slate-900/50' : 'bg-slate-50'}>
                  <th className={`text-left px-3 py-2 text-[10px] font-semibold min-w-[140px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Indicator</th>
                  {visibleDevs.map(name => (
                    <th key={name} className={`text-center px-2 py-2 text-[10px] font-semibold min-w-[60px] capitalize ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{name.split(' ')[0]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {perUserIndicators.map(indicator => {
                  const Icon = INDICATOR_ICONS[indicator.id] || mkIcon(faChartBar);
                  return (
                    <tr key={indicator.id} className={`border-t ${isDark ? 'border-slate-700/15 hover:bg-slate-700/10' : 'border-slate-100 hover:bg-slate-50'}`}>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <Icon className={`h-3 w-3 ${isDark ? 'text-slate-500' : 'text-slate-400'} flex-shrink-0`} />
                          <span className={`text-[11px] ${isDark ? 'text-slate-300' : 'text-slate-700'} truncate`}>{indicator.name}</span>
                        </div>
                      </td>
                      {visibleDevs.map(name => {
                        const result = getDevResult(indicator, name);
                        if (!result) return <td key={name} className={`px-2 py-1.5 text-center ${isDark ? 'text-slate-700' : 'text-slate-300'}`}>—</td>;
                        const colors = SEVERITY_COLORS[result.severity];
                        return (
                          <td key={name} className="px-2 py-1.5 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>{result.label}</span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {hiddenCount > 0 && (
              <div className={`border-t ${isDark ? 'border-slate-700/20' : 'border-slate-100'} px-3 py-1.5 text-center`}>
                <button onClick={() => setShowAllUsers(!showAllUsers)} className="text-[10px] text-blue-400 hover:text-blue-300">
                  {showAllUsers ? 'Show fewer' : `+${hiddenCount} more users`}
                </button>
              </div>
            )}
          </div>
        )}
        {serverLoading && perUserIndicators.length === 0 && (
          <div className="flex items-center justify-center gap-2 py-4">
            <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 text-blue-400 animate-spin" />
            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Calculating per user indicators...</span>
          </div>
        )}
      </div>
    );
  };

  // Per-developer table for server-calculated Jira LEAD indicators
  const renderServerLeadTable = () => {
    const perUserLeadIndicators = serverLeadIndicators.filter(i => (i.scope || 'per-user') === 'per-user');
    if (perUserLeadIndicators.length === 0) return null;

    const devActivity = new Map<string, number>();
    for (const ind of perUserLeadIndicators) {
      for (const r of ind.results) {
        devActivity.set(r.developerName, (devActivity.get(r.developerName) || 0) + Math.abs(r.value));
      }
    }
    const allDevNames = Array.from(devActivity.entries()).sort((a, b) => b[1] - a[1]).map(([name]) => name);
    const visibleDevs = showAllUsers ? allDevNames : allDevNames.slice(0, 6);
    const hiddenCount = allDevNames.length - 6;
    const getDevResult = (indicator: LagIndicatorResult, devName: string) => indicator.results.find(r => r.developerName === devName);

    const sGood = perUserLeadIndicators.filter(i => i.severity === 'good').length;
    const sWarn = perUserLeadIndicators.filter(i => i.severity === 'warning').length;
    const sCrit = perUserLeadIndicators.filter(i => i.severity === 'critical').length;

    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 px-1">
          <FontAwesomeIcon icon={faArrowTrendUp} className="h-3.5 w-3.5 text-emerald-400" />
          <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Per User Lead Indicators</span>
          <div className="flex items-center gap-1 ml-auto">
            {sGood > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">{sGood} ✓</span>}
            {sWarn > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/20">{sWarn} ⚠</span>}
            {sCrit > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/20">{sCrit} ✗</span>}
          </div>
        </div>
        <div className={`overflow-x-auto rounded-lg border ${isDark ? 'border-slate-700/30' : 'border-slate-200'}`}>
          <table className="w-full text-[11px]">
            <thead>
              <tr className={isDark ? 'bg-slate-900/50' : 'bg-slate-50'}>
                <th className={`text-left px-3 py-2 text-[10px] font-semibold min-w-[140px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Indicator</th>
                {visibleDevs.map(name => (
                  <th key={name} className={`text-center px-2 py-2 text-[10px] font-semibold min-w-[60px] capitalize ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{name.split(' ')[0]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {perUserLeadIndicators.map(indicator => {
                const Icon = INDICATOR_ICONS[indicator.id] || mkIcon(faArrowTrendUp);
                return (
                  <tr key={indicator.id} className={`border-t ${isDark ? 'border-slate-700/15 hover:bg-slate-700/10' : 'border-slate-100 hover:bg-slate-50'}`}>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <Icon className={`h-3 w-3 ${isDark ? 'text-slate-500' : 'text-slate-400'} flex-shrink-0`} />
                        <span className={`text-[11px] ${isDark ? 'text-slate-300' : 'text-slate-700'} truncate`}>{indicator.name}</span>
                      </div>
                    </td>
                    {visibleDevs.map(name => {
                      const result = getDevResult(indicator, name);
                      if (!result) return <td key={name} className={`px-2 py-1.5 text-center ${isDark ? 'text-slate-700' : 'text-slate-300'}`}>—</td>;
                      const colors = SEVERITY_COLORS[result.severity];
                      return (
                        <td key={name} className="px-2 py-1.5 text-center">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>{result.label}</span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {hiddenCount > 0 && (
            <div className={`border-t ${isDark ? 'border-slate-700/20' : 'border-slate-100'} px-3 py-1.5 text-center`}>
              <button onClick={() => setShowAllUsers(!showAllUsers)} className="text-[10px] text-blue-400 hover:text-blue-300">
                {showAllUsers ? 'Show fewer' : `+${hiddenCount} more users`}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'}`}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faShieldHalved} className={`h-3.5 w-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <span className={`text-xs font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'} uppercase tracking-wider`}>Health Indicators</span>
          <span className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>{allIndicators.length} metrics</span>
        </div>
        <div className="flex items-center gap-1.5">
          {goodCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">{goodCount} healthy</span>}
          {warningCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/20">{warningCount} warning</span>}
          {criticalCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/20">{criticalCount} critical</span>}
        </div>
      </div>

      {/* Aggregate indicator tables in 2-col grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {renderTable('Git Lead Indicators', mkIcon(faArrowTrendUp), gitLead, 'lead')}
        {renderTable('Overall Jira Lead Indicators', mkIcon(faArrowTrendUp), jiraLead, 'lead')}
        {renderTable('Git Lag Indicators', mkIcon(faArrowTrendDown), gitLag, 'lag')}
        {renderTable('Overall Jira Lag Indicators', mkIcon(faArrowTrendDown), jiraLag, 'lag')}
      </div>

      {/* Server-calculated per-user indicators (developer columns — side by side) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {renderServerLeadTable()}
        {renderServerLagTable()}
      </div>
    </div>
  );
}

export default CombinedIndicators;
